<script setup lang="ts">
import { computed, onMounted, toRaw, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useADialog, useAToast } from '@Atoms';
import { getDefaultDateFormat, scrollToTop } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useForm } from '@FormModule';
import { GetAnimalAdoptionFees, GetAnimalOwnerRequestedEuthanasiaFees } from '@AnimalProfileModule';
import getSanitizedPayload from './getSanitizedPayload';
import useCartStore from '../../../../store';
import useFields from './useFields';
import useFieldsOutcomeDate from './useFieldsOutcomeDate';
import {
  AddOutcomeToCart,
  CompleteOutcomeService,
  GetOutcomeFormByName,
  GetWaiversService,
} from '../../../../services';
import { CART_STEPS } from '../../../../types';
import { useCartSteps } from '../../../../composables';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const {
  animals,
  cartData,
  cartLength,
  completeOutcomeRoute,
  donations,
  isAdoption,
  isIntake,
  isEuthanasia,
  loading,
  outcomeForm,
  outcomeInfoActiveStep,
  productsLength,
  rtoFees,
  servicesLength,
  stepState,
} = storeToRefs(useCartStore());
const { userProfile } = storeToRefs(useSessionStore());
const { outcomeCompletedCartReset } = useCartStore();

const { activeModal, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, nextStep, nextStepButton } = useCartSteps();
const { fieldsValidation } = useForm();
const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();
const {
  additionalFeesOptions,
  adoptionFeesOptions,
  euthanasiaFeesOptions,
  currentDataOnStepperId,
  currentSchemaModels,
  fieldsSchema,
  formError,
  outcomeSchemaModels,
  sourceData,
  waivers,
  setAdditionalFeeField,
  setForm,
} = useFields();
const {
  currentSchemaModels: currentSchemaModelsOutcomeDate,
  fieldsSchema: fieldsSchemaOutcomeDate,
  setForm: setFormOutcomeDate,
  userCanSetPaidDate,
} = useFieldsOutcomeDate();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const onlyAnimalsCartLengh = computed(
  () => cartLength.value - productsLength.value - servicesLength.value - donations.value.length
);
const currentAnimalData = computed(() =>
  animals.value.find((animal: any) => animal._id === currentDataOnStepperId.value)
);
const animalCartStepId = computed(() => animals.value[outcomeInfoActiveStep.value]._id);

onMounted(async () => await onInitPage());

watch(animalCartStepId, () => onAnimalCartStepIdChange());
watch(currentDataOnStepperId, () => getUpdatedFeesOptions());

// HELPERS
function getAdoptionFees() {
  GetAnimalAdoptionFees(currentAnimalData?.value?.species).then((result: any) => {
    adoptionFeesOptions.value = result.map((fee: any) => {
      let amount = fee.amount;

      if (typeof amount === 'number') {
        const parts = amount.toString().split('.');
        if (!parts[1] || parts[1].length < 2) {
          amount = amount.toFixed(2);
        }
      }

      return {
        ...fee,
        amount,
        label: `${fee.description} - $${amount}`,
      };
    });
  });
}

function getEuthanasiaFees() {
  GetAnimalOwnerRequestedEuthanasiaFees(currentAnimalData?.value?.species).then((result: any) => {
    euthanasiaFeesOptions.value = result.map((fee: any) => {
      return { ...fee, label: `${fee.description} - $${fee.amount}` };
    });
  });
}

function setCurrentDataStepperId() {
  currentDataOnStepperId.value = animals.value[outcomeInfoActiveStep.value]._id;
}

async function setOutcomeForms() {
  if (!cartData.value.outcome_type) return;
  loading.value = true;

  setCurrentDataStepperId();

  await GetWaiversService('outcome').then((waiver: any) => (waivers.value = waiver));
  await GetOutcomeFormByName(cartData.value.outcome_type).then((data) => {
    outcomeForm.value = data;

    const outcomeFormNotReady = !outcomeForm.value?.form?.fields;
    if (outcomeFormNotReady) return setOutcomeForms();

    // NOTE: We use this rtoFees state to repopulate the select RTO Fees field after hard refresh
    const hasRtoFees = outcomeForm.value?.rto_fees.length;
    if (hasRtoFees) rtoFees.value = outcomeForm.value.rto_fees;

    sourceData.value = {
      rawFields: outcomeForm.value?.form.fields,
      additional_fees_options: outcomeForm.value?.additional_fees,
      partners: outcomeForm.value?.partners,
      rto_fees: outcomeForm.value?.rto_fees,
    };
  });

  // TODO :: AF-587 We need to have a outcome_date per animal and not per outcome
  const oldestIntakeDate = animals.value.reduce((oldest: any, animal: any) => {
    return !oldest || new Date(animal.intake_date) < new Date(oldest) ? animal.intake_date : oldest;
  }, null);

  setFormOutcomeDate(oldestIntakeDate);
  setForm();

  loading.value = false;
}

function validateForms() {
  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  const noOutcomeDate = fieldsValidation(fieldsSchemaOutcomeDate.value, currentSchemaModelsOutcomeDate.value);

  if (hasErrors || noOutcomeDate) return (formError.value = true);

  const rawSet = toRaw(currentSchemaModels.value);
  const currentSchemaModelsClone = new Set(Array.from(rawSet).map((obj) => cloneDeep(toRaw(obj))));

  outcomeSchemaModels.value[outcomeInfoActiveStep.value] = currentSchemaModelsClone;

  formError.value = false;
}

function getUpdatedFeesOptions() {
  if (isAdoption.value) getAdoptionFees();
  if (isEuthanasia.value && isIntake.value) getEuthanasiaFees();
}

// EVENTS
function onAnimalCartStepIdChange() {
  setCurrentDataStepperId();
  getUpdatedFeesOptions();
  setAdditionalFeeField(additionalFeesOptions.value);
}

async function onInitPage() {
  outcomeInfoActiveStep.value = 0;
  currentStep.value = CART_STEPS.OUTCOME_INFO;
  await setOutcomeForms();
}

function onNextAnimal(action: Function) {
  validateForms();

  if (formError.value) return;
  scrollToTop();
  action();
}

function onNextStep() {
  validateForms();

  if (formError.value) return;

  loading.value = true;

  const [outcomeDate, paidDate = { value: null }] = currentSchemaModelsOutcomeDate.value;
  // NOTE: this is the format being used at adopt.animalsfirst
  const adoptAppOutcomeDate = moment(outcomeDate.value).format('YYYY-MM-DD');

  const payload = animals.value.map((animal: any, index: number) => {
    const outcome = getSanitizedPayload(outcomeSchemaModels.value[index]);

    // NOTE: Removing rto_fees. Not used in the backend.
    delete (outcome as Partial<typeof outcome>).rto_fees;
    const { waivers, ...outcomeData } = outcome;

    return {
      ...animal,
      outcome_date: adoptAppOutcomeDate,
      intake_date: animal.intake_date ? getDefaultDateFormat(animal.intake_date) : null,
      paid_date: paidDate.value ? getDefaultDateFormat(paidDate.value) : null,
      waivers,
      outcome: {
        ...outcomeData,
        animal_id: animal._id,
        animal_name: animal.animal_name,
        form_id: outcomeForm.value.form._id.$oid,
        form_title: outcomeForm.value.form.form_title,
        outcome_type: cartData.value.outcome_type,
        outcome_date: adoptAppOutcomeDate,
        partner_name: outcomeForm.value.partners.find((field: any) => field.id === outcome.partner_name),
      },
    };
  });

  AddOutcomeToCart(payload)
    .then((data) => {
      cartData.value = data;

      if (!nextStep.value.step) return onCompleteOutcome(data);

      emits('onStepUpdate', nextStep.value.step!.name);
      stepState.value![CART_STEPS.OUTCOME_INFO] = payload;
      loading.value = false;
    })
    .catch(() => notify(TOAST_ERROR) && (loading.value = false));
}

function onCompleteOutcome(payload: any) {
  // NOTE :: TS compliance - completeOutcomeRoute should always exist when completing an outcome
  if (!completeOutcomeRoute.value) {
    Sentry.captureException(new Error('completeOutcomeRoute is null during onCompleteOutcome'), {
      tags: { component: 'MCartStepOutcomeInfo', type: 'ts-compliance' },
    });
  }

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  CompleteOutcomeService(payload)
    .then(() => push(completeOutcomeRoute.value!).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div class="flex h-full max-w-[700px] flex-col gap-4">
    <FormTemplate
      class="w-full"
      formError=""
      :schema="fieldsSchemaOutcomeDate"
    >
      <Message
        v-if="userCanSetPaidDate"
        pt:text="text-sm"
        severity="info"
        :closable="false"
      >
        {{ $t('cart.label.retroactiveOutcome', { firstName: userProfile?.first_name }) }}
      </Message>
    </FormTemplate>

    <div class="flex items-center gap-4">
      <h3 class="block font-semibold">{{ $t('common.details') }}</h3>

      <Divider
        class="before:border-t-2"
        type="dashed"
      />
    </div>

    <Stepper
      v-model:activeStep="outcomeInfoActiveStep"
      linear
      pt:header="px-0 flex-[0]"
      pt:nav="justify-start"
      pt:panelContainer="px-0"
    >
      <StepperPanel
        v-for="animal in animals"
        :key="animal._id"
      >
        <template #header="{ index, clickCallback }">
          <div class="flex w-full items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <Button
                rounded
                class="grid !h-8 !w-5 place-content-center border-none bg-brand-500 text-sm"
                :label="(index + 1).toString()"
                @click="clickCallback"
              />

              <Tag
                severity="warning"
                :value="animal.animal_name"
                @click="clickCallback"
              />
            </div>
          </div>
        </template>

        <template #content="{ index, nextCallback, prevCallback }">
          <div class="scrollToTop flex flex-col gap-4">
            <div
              class="surface-border border-round surface-ground justify-content-center align-items-center flex flex-auto border-2 border-dashed p-4 font-medium"
            >
              <FormTemplate
                class="w-full"
                :formError="animal._id === animalCartStepId ? formError : false"
                :schema="fieldsSchema"
              />
            </div>

            <AFormError :formError />
          </div>

          <div class="flex py-4">
            <Button
              v-if="onlyAnimalsCartLengh > 1 && index > 0"
              outlined
              class="af-button af-button-success-outlined mr-auto"
              icon="pi pi-arrow-left"
              :label="$t('common.previous')"
              @click="prevCallback"
            />

            <Button
              v-if="index + 1 < onlyAnimalsCartLengh"
              outlined
              class="af-button af-button-success-outlined ml-auto"
              icon="pi pi-arrow-right"
              :label="$t('common.next')"
              @click="onNextAnimal(nextCallback)"
            />

            <Button
              v-else
              class="af-button af-button-success ml-auto"
              iconPos="right"
              :label="$t(nextStepButton.label)"
              @click="onNextStep"
            >
              <template #icon>
                <FaIcon
                  v-if="nextStepButton.icon"
                  class="mr-2"
                  :icon="$icons[nextStepButton.icon]"
                />
              </template>
            </Button>
          </div>
        </template>
      </StepperPanel>
    </Stepper>
  </div>
</template>
